export class PositionObserver {
  positionObserverCallback: (data: {
    x: number
    y: number
    target: Element
    outOfViewport: boolean
    rootBounds: DOMRectReadOnly | null
  }) => void
  thresholdList: number[]
  intersectionObserver?: IntersectionObserver

  constructor(
    positionObserverCallback: (data: {
      x: number
      y: number
      target: Element
      outOfViewport: boolean
      rootBounds: DOMRectReadOnly | null
    }) => void,
    thresholdFraction = 1000,
  ) {
    this.positionObserverCallback = positionObserverCallback
    this.thresholdList = new Array(thresholdFraction + 1)
      .fill(0)
      .map((_, ind) => ind / thresholdFraction)
  }
  observe(targetElement: Element) {
    this.intersectionObserver = new IntersectionObserver(
      this.viewportCallback.bind(this),
      {
        threshold: this.thresholdList,
        rootMargin: '0px 0px 0px 0px',
        root: document,
      },
    )
    this.intersectionObserver.observe(targetElement)
  }
  viewportCallback(e: IntersectionObserverEntry[]) {
    const entry = e[0]
    const target = entry.target
    const targetBounds = entry.boundingClientRect
    if (this.positionObserverCallback) {
      this.positionObserverCallback({
        x: targetBounds.left,
        y: targetBounds.top,
        target,
        outOfViewport: !entry.isIntersecting,
        rootBounds: entry.rootBounds,
      })
    }
    if (entry.intersectionRatio > 0) {
      this.intersectionObserver?.unobserve(target)
      this.intersectionObserver?.disconnect()
      const options = {
        threshold: this.thresholdList,
        rootMargin: this.getMargins(
          this.constructBoxWindow(targetBounds, entry.rootBounds),
          entry.rootBounds,
        ),
        root: document,
      }
      this.intersectionObserver = new IntersectionObserver(
        this.intersectionObsCallback.bind(this),
        options,
      )
      this.intersectionObserver?.observe(target)
    }
  }
  intersectionObsCallback(e: IntersectionObserverEntry[]) {
    const entry = e[0]
    const target = entry.target
    const targetBounds = entry.boundingClientRect
    if (this.positionObserverCallback) {
      this.positionObserverCallback({
        x: targetBounds.left,
        y: targetBounds.top,
        target,
        outOfViewport: false,
        rootBounds: entry.rootBounds,
      })
    }
    if (entry.intersectionRatio === 0) {
      this.intersectionObserver?.unobserve(target)
      this.intersectionObserver?.disconnect()
      this.intersectionObserver = new IntersectionObserver(
        this.viewportCallback.bind(this),
        {
          threshold: this.thresholdList,
          rootMargin: '0px 0px 0px 0px',
          root: document,
        },
      )
      this.intersectionObserver?.observe(target)
    }
  }
  disconnect() {
    this.intersectionObserver?.disconnect()
  }
  constructBoxWindow(
    targetBounds: DOMRectReadOnly,
    rootBounds: DOMRectReadOnly,
  ) {
    const constrainedLeft = Math.min(
      Math.max(rootBounds.left, targetBounds.left),
      rootBounds.right - targetBounds.width,
    )
    const constrainedTop = Math.min(
      Math.max(rootBounds.top, targetBounds.top),
      rootBounds.bottom - targetBounds.height,
    )
    const constrainedRight = constrainedLeft + targetBounds.width
    const constrainedBottom = constrainedTop + targetBounds.height
    return {
      left: constrainedLeft,
      top: constrainedTop,
      right: constrainedRight,
      bottom: constrainedBottom,
    }
  }
  getMargins(
    windowDimensions: {
      left: number
      top: number
      right: number
      bottom: number
    },
    rootBounds: DOMRectReadOnly | null,
  ) {
    let viewportBounds
    if (!rootBounds) {
      viewportBounds = {
        top: visualViewport.offsetTop,
        left: visualViewport.offsetLeft,
        right: visualViewport.width + visualViewport.offsetLeft,
        bottom: visualViewport.height + visualViewport.offsetTop,
      }
    } else {
      viewportBounds = rootBounds
    }
    const constrainedTopMarginOfWindow = Math.min(
      -(windowDimensions.top - 1 - viewportBounds.top),
      0,
    )
    const constrainedRightMarginOfWindow = Math.min(
      -(viewportBounds.right - (windowDimensions.right + 1)),
      0,
    )
    const constrainedBottomMarginOfWindow = Math.min(
      -(viewportBounds.bottom - (windowDimensions.bottom + 1)),
      0,
    )
    const constrainedLeftMarginOfWindow = Math.min(
      -(windowDimensions.left - 1 - viewportBounds.left),
      0,
    )
    return `${constrainedTopMarginOfWindow}px ${constrainedRightMarginOfWindow}px ${constrainedBottomMarginOfWindow}px ${constrainedLeftMarginOfWindow}px`
  }
}
